/**
 * WorkspaceManager.ts - Manages workspaces, views, and datasets
 * 
 * CHANGE HISTORY:
 * - 251031_1135 danielbeer: Initial creation for workspace architecture
 */

import type { Aggregatr } from './Aggregatr';
import type {
  WorkspaceConfig,
  ViewConfig,
  DatasetConfig,
  DataSourceConfig,
  WorkspaceState,
  WorkspaceManagerOptions,
  WorkspaceStorage,
  WorkspaceChangeEvent
} from './types/WorkspaceTypes';

/**
 * Default localStorage-based workspace storage
 */
class LocalStorageWorkspaceStorage implements WorkspaceStorage {
  private storageKey: string;
  private stateKey: string;

  constructor(appId: string) {
    this.storageKey = `aggregatr:workspaces:${appId}`;
    this.stateKey = `aggregatr:state:${appId}`;
  }

  async getWorkspaces(): Promise<WorkspaceConfig[]> {
    const data = localStorage.getItem(this.storageKey);
    return data ? JSON.parse(data) : [];
  }

  async getWorkspace(id: string): Promise<WorkspaceConfig | null> {
    const workspaces = await this.getWorkspaces();
    return workspaces.find(w => w.id === id) || null;
  }

  async saveWorkspace(workspace: WorkspaceConfig): Promise<WorkspaceConfig> {
    const workspaces = await this.getWorkspaces();
    const index = workspaces.findIndex(w => w.id === workspace.id);
    
    if (index >= 0) {
      workspaces[index] = workspace;
    } else {
      workspaces.push(workspace);
    }
    
    localStorage.setItem(this.storageKey, JSON.stringify(workspaces));
    return workspace;
  }

  async deleteWorkspace(id: string): Promise<boolean> {
    const workspaces = await this.getWorkspaces();
    const filtered = workspaces.filter(w => w.id !== id);
    
    if (filtered.length === workspaces.length) {
      return false; // Not found
    }
    
    localStorage.setItem(this.storageKey, JSON.stringify(filtered));
    return true;
  }

  async getState(): Promise<WorkspaceState | null> {
    const data = localStorage.getItem(this.stateKey);
    return data ? JSON.parse(data) : null;
  }

  async saveState(state: WorkspaceState): Promise<void> {
    localStorage.setItem(this.stateKey, JSON.stringify(state));
  }
}

/**
 * WorkspaceManager - Manages workspace/view/dataset hierarchy
 */
export class WorkspaceManager {
  private aggregatr: Aggregatr;
  private storage: WorkspaceStorage;
  private options: WorkspaceManagerOptions;
  private appId: string;
  private debug: boolean;
  private autoSave: boolean;
  
  private workspaces: Map<string, WorkspaceConfig>;
  private currentState: WorkspaceState | null;
  private listeners: Set<(event: WorkspaceChangeEvent) => void>;

  constructor(aggregatr: Aggregatr, options: WorkspaceManagerOptions) {
    this.aggregatr = aggregatr;
    this.options = options; // Store options for later use
    this.appId = options.appId;
    this.storage = options.storage || new LocalStorageWorkspaceStorage(options.appId);
    this.debug = options.debug || false;
    this.autoSave = options.autoSave !== false; // Default true
    
    this.workspaces = new Map();
    this.currentState = null;
    this.listeners = new Set();
  }

  /**
   * Initialize workspace manager
   */
  async initialize(): Promise<void> {
    if (this.debug) {
      console.log(`[WorkspaceManager] Initializing for app: ${this.appId}`);
    }

    // Load workspaces from storage
    const workspaces = await this.storage.getWorkspaces();
    workspaces.forEach(workspace => {
      this.workspaces.set(workspace.id, workspace);
    });

    // Load current state
    this.currentState = await this.storage.getState();

    // If no state, create default workspace (unless skipped)
    if (!this.options.skipDefaultWorkspace && (!this.currentState || this.workspaces.size === 0)) {
      await this.createDefaultWorkspace();
    }

    // Validate current state
    if (this.currentState) {
      const workspace = this.workspaces.get(this.currentState.currentWorkspaceId);
      if (!workspace) {
        // Current workspace doesn't exist, reset to default (unless skipped)
        if (!this.options.skipDefaultWorkspace) {
          await this.createDefaultWorkspace();
        }
      } else {
        // Apply current dataset to Aggregatr
        await this.applyCurrentDataset();
      }
    }

    if (this.debug) {
      console.log(`[WorkspaceManager] Initialized with ${this.workspaces.size} workspaces`);
    }
  }

  /**
   * Create default workspace
   */
  private async createDefaultWorkspace(): Promise<void> {
    const now = new Date().toISOString();
    const workspaceId = this.generateId();
    const viewId = this.generateId();
    const datasetId = this.generateId();

    const workspace: WorkspaceConfig = {
      id: workspaceId,
      name: 'Default',
      description: 'Default workspace',
      icon: '📁',
      views: [
        {
          id: viewId,
          name: 'Default View',
          type: 'list',
          icon: '📋',
          dataset: {
            id: datasetId,
            name: 'Default Dataset',
            description: 'Default dataset with local storage',
            sources: [
              {
                name: 'local',
                type: 'local',
                config: {},
                entityTypes: [],
                primary: true,
                enabled: true
              }
            ],
            mergeStrategy: 'first',
            createdAt: now,
            updatedAt: now
          },
          createdAt: now,
          updatedAt: now
        }
      ],
      defaultViewId: viewId,
      createdAt: now,
      updatedAt: now
    };

    await this.saveWorkspace(workspace);

    this.currentState = {
      currentWorkspaceId: workspaceId,
      currentViewId: viewId,
      currentDatasetId: datasetId,
      lastAccessed: now,
      appId: this.appId
    };

    if (this.autoSave) {
      await this.storage.saveState(this.currentState);
    }

    await this.applyCurrentDataset();
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get all workspaces
   */
  getWorkspaces(): WorkspaceConfig[] {
    return Array.from(this.workspaces.values());
  }

  /**
   * Get a specific workspace
   */
  getWorkspace(id: string): WorkspaceConfig | null {
    return this.workspaces.get(id) || null;
  }

  /**
   * Get current workspace
   */
  getCurrentWorkspace(): WorkspaceConfig | null {
    if (!this.currentState) return null;
    return this.workspaces.get(this.currentState.currentWorkspaceId) || null;
  }

  /**
   * Get current view
   */
  getCurrentView(): ViewConfig | null {
    const workspace = this.getCurrentWorkspace();
    if (!workspace || !this.currentState) return null;
    return workspace.views.find(v => v.id === this.currentState!.currentViewId) || null;
  }

  /**
   * Get current dataset
   */
  getCurrentDataset(): DatasetConfig | null {
    const view = this.getCurrentView();
    return view?.dataset || null;
  }

  /**
   * Get current state
   */
  getCurrentState(): WorkspaceState | null {
    return this.currentState;
  }

  /**
   * Create a new workspace
   */
  async createWorkspace(config: Partial<WorkspaceConfig>): Promise<WorkspaceConfig> {
    const now = new Date().toISOString();
    const workspace: WorkspaceConfig = {
      id: config.id || this.generateId(),
      name: config.name || 'New Workspace',
      description: config.description,
      icon: config.icon || '📁',
      views: config.views || [],
      defaultViewId: config.defaultViewId,
      createdAt: now,
      updatedAt: now,
      createdBy: config.createdBy,
      updatedBy: config.updatedBy
    };

    await this.saveWorkspace(workspace);

    this.emit({
      type: 'workspace',
      previous: null,
      current: workspace,
      timestamp: now
    });

    return workspace;
  }

  /**
   * Update a workspace
   */
  async updateWorkspace(id: string, updates: Partial<WorkspaceConfig>): Promise<WorkspaceConfig> {
    const workspace = this.workspaces.get(id);
    if (!workspace) {
      throw new Error(`Workspace ${id} not found`);
    }

    const previous = { ...workspace };
    const updated: WorkspaceConfig = {
      ...workspace,
      ...updates,
      id, // Preserve ID
      updatedAt: new Date().toISOString()
    };

    await this.saveWorkspace(updated);

    this.emit({
      type: 'workspace',
      previous,
      current: updated,
      timestamp: updated.updatedAt
    });

    return updated;
  }

  /**
   * Delete a workspace
   */
  async deleteWorkspace(id: string): Promise<boolean> {
    const workspace = this.workspaces.get(id);
    if (!workspace) {
      return false;
    }

    // Can't delete if it's the current workspace
    if (this.currentState?.currentWorkspaceId === id) {
      throw new Error('Cannot delete current workspace. Switch to another workspace first.');
    }

    await this.storage.deleteWorkspace(id);
    this.workspaces.delete(id);

    if (this.debug) {
      console.log(`[WorkspaceManager] Deleted workspace: ${id}`);
    }

    return true;
  }

  /**
   * Save workspace to storage
   */
  private async saveWorkspace(workspace: WorkspaceConfig): Promise<void> {
    this.workspaces.set(workspace.id, workspace);
    await this.storage.saveWorkspace(workspace);

    if (this.debug) {
      console.log(`[WorkspaceManager] Saved workspace: ${workspace.name}`);
    }
  }

  /**
   * Switch to a different workspace
   */
  async switchWorkspace(workspaceId: string): Promise<void> {
    const workspace = this.workspaces.get(workspaceId);
    if (!workspace) {
      throw new Error(`Workspace ${workspaceId} not found`);
    }

    const previousWorkspace = this.getCurrentWorkspace();
    
    // Get default view or first view
    const view = workspace.views.find(v => v.id === workspace.defaultViewId) || workspace.views[0];
    if (!view) {
      throw new Error(`Workspace ${workspaceId} has no views`);
    }

    this.currentState = {
      currentWorkspaceId: workspaceId,
      currentViewId: view.id,
      currentDatasetId: view.dataset.id,
      lastAccessed: new Date().toISOString(),
      appId: this.appId
    };

    if (this.autoSave) {
      await this.storage.saveState(this.currentState);
    }

    await this.applyCurrentDataset();

    this.emit({
      type: 'workspace',
      previous: previousWorkspace,
      current: workspace,
      timestamp: this.currentState.lastAccessed
    });

    if (this.debug) {
      console.log(`[WorkspaceManager] Switched to workspace: ${workspace.name}`);
    }
  }

  /**
   * Switch to a different view
   */
  async switchView(viewId: string): Promise<void> {
    const workspace = this.getCurrentWorkspace();
    if (!workspace) {
      throw new Error('No current workspace');
    }

    const view = workspace.views.find(v => v.id === viewId);
    if (!view) {
      throw new Error(`View ${viewId} not found in current workspace`);
    }

    const previousView = this.getCurrentView();

    this.currentState = {
      ...this.currentState!,
      currentViewId: viewId,
      currentDatasetId: view.dataset.id,
      lastAccessed: new Date().toISOString()
    };

    if (this.autoSave) {
      await this.storage.saveState(this.currentState);
    }

    await this.applyCurrentDataset();

    this.emit({
      type: 'view',
      previous: previousView,
      current: view,
      timestamp: this.currentState.lastAccessed
    });

    if (this.debug) {
      console.log(`[WorkspaceManager] Switched to view: ${view.name}`);
    }
  }

  /**
   * Apply current dataset to Aggregatr
   */
  private async applyCurrentDataset(): Promise<void> {
    const dataset = this.getCurrentDataset();
    if (!dataset) {
      if (this.debug) {
        console.warn('[WorkspaceManager] No current dataset to apply');
      }
      return;
    }

    // Clear existing data sources
    const existingSources = this.aggregatr.getDataSources();
    existingSources.forEach(source => {
      this.aggregatr.removeDataSource(source.name);
    });

    // Add data sources from current dataset
    for (const sourceConfig of dataset.sources) {
      if (!sourceConfig.enabled) continue;

      // Create storage adapter based on source type
      const adapter = await this.createStorageAdapter(sourceConfig);
      
      this.aggregatr.addDataSource(sourceConfig.name, {
        name: sourceConfig.name,
        adapter,
        priority: sourceConfig.priority
      });
    }

    if (this.debug) {
      console.log(`[WorkspaceManager] Applied dataset: ${dataset.name} with ${dataset.sources.length} sources`);
    }
  }

  /**
   * Create storage adapter for data source
   * This is a placeholder - actual implementation will be in separate adapters
   */
  private async createStorageAdapter(_config: DataSourceConfig): Promise<any> {
    // TODO: Implement actual adapter creation based on _config.type
    // For now, return a placeholder that uses the existing storage
    return this.aggregatr['storage']; // Access private storage temporarily
  }

  /**
   * Add event listener
   */
  on(listener: (event: WorkspaceChangeEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Emit event to listeners
   */
  private emit(event: WorkspaceChangeEvent): void {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('[WorkspaceManager] Error in event listener:', error);
      }
    });
  }

  /**
   * Export workspace configuration
   */
  exportWorkspace(workspaceId: string): string {
    const workspace = this.workspaces.get(workspaceId);
    if (!workspace) {
      throw new Error(`Workspace ${workspaceId} not found`);
    }
    return JSON.stringify(workspace, null, 2);
  }

  /**
   * Import workspace configuration
   */
  async importWorkspace(json: string): Promise<WorkspaceConfig> {
    const workspace = JSON.parse(json) as WorkspaceConfig;
    
    // Generate new IDs to avoid conflicts
    workspace.id = this.generateId();
    workspace.views.forEach(view => {
      view.id = this.generateId();
      view.dataset.id = this.generateId();
    });

    await this.saveWorkspace(workspace);
    return workspace;
  }
}

export default WorkspaceManager;
